"use client";

import "react-phone-number-input/style.css";
import {
    phoneInputContainerStyle,
    phoneInputHintStyle,
} from "@/component-library/components/user-input/phone-input/PhoneInputStyles.css";
import {
    isMalformedPhoneNumber,
    phoneNumberHint,
} from "@/component-library/components/user-input/phone-input/phoneNumberValidation";
import { useBrowserCountry } from "@/common/utilities/locale/useBrowserCountry";
import { CountryCode, E164Number } from "libphonenumber-js";
import * as ReactPhoneInput from "react-phone-number-input";
import React, { FC, useState } from "react";

// A handful of ISO countries carry no phone metadata -- Antarctica, Pitcairn
// and the other near-uninhabited territories. Detection can legitimately land on
// one, and pre-selecting it would be rejected by the input anyway, so treat
// those as "not detected" and let the caller's fallback stand.
const countriesWithPhoneMetadata = new Set<string>(
    ReactPhoneInput.getCountries()
);

export const PhoneInputComponent: FC<{
    /**
     * Country to pre-select when the visitor's own country cannot be detected
     * from their browser. The detected country wins over this: the number being
     * entered belongs to the person filling in the form, not to whatever the
     * surrounding page happens to be about.
     *
     * Getting this wrong is not cosmetic. The input is international-only, so
     * the pre-selected country's calling code prefixes whatever is typed, and a
     * national number entered under the wrong prefix silently becomes a
     * different, dialable-looking number.
     */
    defaultCountry?: string;
    value?: string;
    onChange: (value?: E164Number) => void;
}> = ({ defaultCountry, value, onChange }) => {
    // Undefined until after mount (the server has no visitor locale), so the
    // first render shows the caller's fallback. The input swaps to the detected
    // country when it arrives, but only while no number has been entered and no
    // country picked by hand -- so this never overwrites real input.
    const browserCountry = useBrowserCountry();
    const detectedCountry =
        browserCountry && countriesWithPhoneMetadata.has(browserCountry)
            ? browserCountry
            : undefined;
    const effectiveDefaultCountry = detectedCountry ?? defaultCountry;

    // The input announces its country only once the selection diverges from the
    // default -- on mount, when the two already agree, it stays silent. So null
    // means "never announced" and falls back to the default, which is not the
    // same as the visitor having chosen "International" (announced as
    // undefined) and must not be collapsed into it: the two want different
    // hints.
    const [announcedCountry, setAnnouncedCountry] = useState<
        string | null | undefined
    >(null);
    const selectedCountry =
        announcedCountry === null ? effectiveDefaultCountry : announcedCountry;

    const [isFocused, setIsFocused] = useState(false);

    // Held back while the field has focus, so the hint does not accuse the
    // visitor of a mistake part-way through typing a number that is merely
    // unfinished. Outside that it shows straight away, which also catches a bad
    // number arriving from a caller's prefill.
    const showHint = !isFocused && isMalformedPhoneNumber(value ?? "");

    return (
        <>
            {" "}
            <ReactPhoneInput.default
                international={true}
                className={phoneInputContainerStyle}
                // Empty must reach the input as undefined, never "". The input
                // reports an incomplete number as `undefined`, callers commonly
                // store that as "" (`value ?? ""`), and handing "" back looks
                // to the input like a *different* value arriving from outside,
                // which makes it discard what is being typed and refill the
                // default country's calling code. It only normalises null, not
                // "". Selecting the whole field and typing a new number goes
                // through exactly that state, so without this the first
                // keystroke snaps back to the default country.
                value={value || undefined}
                onChange={onChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onCountryChange={setAnnouncedCountry}
                defaultCountry={effectiveDefaultCountry as CountryCode}
            />
            {showHint && (
                <span className={phoneInputHintStyle} role="alert">
                    {phoneNumberHint(selectedCountry)}
                </span>
            )}
        </>
    );
};
